/**
 * RECORD IDS IN SCOUT'S PROSE, LINKED TO THE PAGE THAT EXPLAINS THEM.
 *
 * WHY THIS IS A NODE-TREE TRANSFORM, NOT A REGEX OVER THE RAW MESSAGE. The
 * Markdown parser has already separated prose from inline code, fenced code
 * and link destinations. Transforming the parsed tree means a `run:` inside
 * backticks or inside an existing link can never become a second link, by
 * construction rather than by a regex that has to remember every exception.
 *
 * WHAT IS LINKED IS A CLOSED LIST. Only ids with a real detail page are in the
 * table; `src:`, `finding:`, `trial:`, `journal:` and every internal prefix
 * stay text. A link that opens a page which cannot explain the id is worse
 * than plain text, because it looks like an answer.
 *
 * THE SHAPES ARE EXACT. The minted ids are `<prefix>:<8 lowercase hex>`
 * (capabilities use 12); a partial or overlong token is not an id and is left
 * alone. The leading boundary is preserved as text; the trailing boundary is a
 * lookahead, so punctuation after an id is not swallowed.
 */

import type { MarkdownBlock, MarkdownInline } from './markdown';
import { pathFor } from './paths';

const RECORD_REF =
	/(^|[^A-Za-z0-9_])(run:[0-9a-f]{8}(?::report)?|ep:(?:run|bridge|mission|scout):[0-9a-f]{8}|topic:[0-9a-f]{8}|space:[0-9a-f]{8}|cap:[0-9a-f]{12}|platform:[A-Z][A-Z0-9_]*|experiment:exp-[0-9a-z]+-[0-9a-f]{8}|exp-[0-9a-z]+-[0-9a-f]{8}|know:[0-9a-f]{8}|scoutcand_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|scoutcand_[0-9a-f]{8})(?=$|[^A-Za-z0-9_])/g;

function hrefFor(ref: string): string | null {
	if (/^run:[0-9a-f]{8}$/.test(ref)) {
		return pathFor({ destination: 'research', section: 'window', recordId: ref });
	}
	if (/^run:[0-9a-f]{8}:report$/.test(ref)) {
		return pathFor({ destination: 'discoveries', section: 'evidence', recordId: ref });
	}
	if (/^ep:/.test(ref)) {
		return pathFor({ destination: 'research', section: 'observatory', recordId: ref });
	}
	if (/^topic:/.test(ref)) {
		return pathFor({ destination: 'research', section: 'topic', recordId: ref });
	}
	if (/^space:/.test(ref)) {
		// THE SPACE ID IS THE SECTION: `/spaces/<id>`.
		return pathFor({ destination: 'spaces', section: ref, recordId: null });
	}
	if (/^(?:cap|platform):/.test(ref)) {
		return pathFor({ destination: 'foundry', section: 'capability', recordId: ref });
	}
	if (/^experiment:/.test(ref)) {
		return pathFor({
			destination: 'research',
			section: 'experiments',
			recordId: ref.slice('experiment:'.length),
		});
	}
	if (/^exp-/.test(ref)) {
		return pathFor({ destination: 'research', section: 'experiments', recordId: ref });
	}
	if (/^know:/.test(ref)) {
		// THE ENTRY IS A CARD ON THE KNOWLEDGE PAGE. The fragment is the card's
		// own DOM id; the view scrolls to it after the first page lands.
		return `${pathFor({ destination: 'discoveries', section: 'knowledge', recordId: null })}#${encodeURIComponent(ref)}`;
	}
	if (/^scoutcand_/.test(ref)) {
		// The proposal page matches a full id or a unique short prefix.
		return pathFor({ destination: 'workspace', section: 'improvements', recordId: ref });
	}
	return null;
}

/** Is the token part of a URL or a path? Bare URLs are text in this renderer;
 *  linking an id inside one would turn a cited address into a second link. */
function insideUrl(text: string, start: number): boolean {
	const before = text.slice(Math.max(0, start - 2_048), start);
	const tokenStart = Math.max(
		before.lastIndexOf(' '),
		before.lastIndexOf('\n'),
		before.lastIndexOf('\t'),
	);
	const prefix = before.slice(tokenStart + 1);
	return /(?:https?:\/\/|www\.)/i.test(prefix) || prefix.startsWith('/');
}

function linkText(text: string): MarkdownInline[] {
	const out: MarkdownInline[] = [];
	let last = 0;
	for (const match of text.matchAll(RECORD_REF)) {
		const index = match.index ?? 0;
		const lead = match[1] ?? '';
		const ref = match[2];
		if (ref === undefined) continue;
		const href = hrefFor(ref);
		const start = index + lead.length;
		if (href === null || insideUrl(text, start)) continue;
		if (start > last) out.push({ kind: 'text', text: text.slice(last, start) });
		out.push({
			kind: 'link',
			href,
			external: false,
			children: [{ kind: 'text', text: ref }],
		});
		last = start + ref.length;
	}
	if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
	return out.length === 0 ? [{ kind: 'text', text }] : out;
}

function linkInline(nodes: readonly MarkdownInline[]): MarkdownInline[] {
	const out: MarkdownInline[] = [];
	for (const node of nodes) {
		if (node.kind === 'text') {
			out.push(...linkText(node.text));
			continue;
		}
		if (node.kind === 'strong' || node.kind === 'em') {
			out.push({ ...node, children: linkInline(node.children) });
			continue;
		}
		// A code span, an existing link and a hard break are left exactly as
		// parsed. The link's own label and href are not revisited.
		out.push(node);
	}
	return out;
}

export function linkRecordRefs(blocks: MarkdownBlock[]): MarkdownBlock[] {
	return blocks.map((block) => {
		switch (block.kind) {
			case 'paragraph':
				return { kind: 'paragraph', children: linkInline(block.children) };
			case 'heading':
				return { kind: 'heading', level: block.level, children: linkInline(block.children) };
			case 'list':
				return { kind: 'list', ordered: block.ordered, start: block.start, items: block.items.map(linkInline) };
			case 'quote':
				return { kind: 'quote', blocks: linkRecordRefs(block.blocks) };
			case 'table':
				return {
					kind: 'table',
					header: block.header.map(linkInline),
					rows: block.rows.map((row) => row.map(linkInline)),
				};
			default:
				return block;
		}
	});
}
